import { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import Potrace from "potrace";
import { finishApplyLoading } from "./stitchApplyLoading";

const ERASE_SCALE = 3; // canvas bitmap pixels per CSS pixel

// ── Split tool visual tutorial ───────────────────────────────────────────────
// A little mouse-cursor glyph used inside the step illustrations.
const SplitCursor = ({ x, y, color = "#7C3AED" }) => (
  <g transform={`translate(${x} ${y})`}>
    <path
      d="M0 0L0 17L4.4 12.8L7.6 19.6L10.2 18.4L7 11.8L13 11.8Z"
      fill="#fff" stroke={color} strokeWidth="1.4" strokeLinejoin="round"
    />
  </g>
);

const SplitTutFrame = ({ children }) => (
  <svg viewBox="0 0 150 96" width="100%" height="auto" style={{ display: "block", background: "rgba(124,58,237,0.05)" }} aria-hidden>
    {children}
  </svg>
);

// Illustrated walkthrough rendered inline above the Split controls so first-time
// users can see the three-step interaction before drawing on the canvas.
const SplitVisualTutorial = ({ coarse }) => {
  const ff = "Clash Grotesk, sans-serif";
  const tap = coarse ? "Tap" : "Click";
  const steps = [
    {
      label: `${tap} just outside one edge of the shape to start.`,
      art: (
        <SplitTutFrame>
          <rect x="42" y="26" width="66" height="44" rx="15" fill="#ede9fe" stroke="#c4b5fd" strokeWidth="2" />
          <circle cx="34" cy="48" r="9.5" fill="none" stroke="#7C3AED" strokeWidth="1.4" opacity="0.4" />
          <circle cx="34" cy="48" r="5" fill="#7C3AED" />
          <SplitCursor x={39} y={52} />
        </SplitTutFrame>
      ),
    },
    {
      label: "Draw all the way across — end just past the opposite edge.",
      art: (
        <SplitTutFrame>
          <rect x="42" y="26" width="66" height="44" rx="15" fill="#ede9fe" stroke="#c4b5fd" strokeWidth="2" />
          <line x1="34" y1="48" x2="116" y2="48" stroke="#7C3AED" strokeWidth="2.2" strokeLinecap="round" strokeDasharray="5 4" />
          <circle cx="34" cy="48" r="4.5" fill="#7C3AED" />
          <circle cx="116" cy="48" r="4.5" fill="#7C3AED" />
          <SplitCursor x={120} y={52} />
        </SplitTutFrame>
      ),
    },
    {
      label: "Press Cut & Apply — the shape splits into separate pieces.",
      art: (
        <SplitTutFrame>
          <path d="M57 26h36a15 15 0 0 1 15 15v3H42v-3a15 15 0 0 1 15-15z" fill="#ede9fe" stroke="#7C3AED" strokeWidth="2" />
          <path d="M42 52h66v3a15 15 0 0 1-15 15H57a15 15 0 0 1-15-15z" fill="#ddd6fe" stroke="#7C3AED" strokeWidth="2" />
          <line x1="46" y1="48" x2="104" y2="48" stroke="#7C3AED" strokeWidth="1.4" strokeDasharray="3 3" opacity="0.55" />
        </SplitTutFrame>
      ),
    },
  ];
  return (
    <div style={{
      background: "#f5f3ff", border: "1px solid #e9e3ff", borderRadius: "9px",
      padding: "10px 11px", display: "flex", flexDirection: "column", gap: "10px",
      fontFamily: ff,
    }}>
      <span style={{ fontSize: "11.5px", fontWeight: 700, color: "#5b4b8a" }}>
        Split a shape into separate pieces
      </span>
      {steps.map((step, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: "11px" }}>
          <div style={{ position: "relative", flexShrink: 0, width: "98px" }}>
            <span style={{
              position: "absolute", top: "-6px", left: "-6px", zIndex: 1,
              width: "20px", height: "20px", borderRadius: "50%",
              background: "#7C3AED", color: "#fff", fontSize: "11px", fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
            }}>
              {i + 1}
            </span>
            <div style={{ border: "1px solid #e9e3ff", borderRadius: "9px", overflow: "hidden" }}>
              {step.art}
            </div>
          </div>
          <span style={{ fontSize: "11px", lineHeight: 1.4, color: "#5b4b8a" }}>{step.label}</span>
        </div>
      ))}
    </div>
  );
};

// ── Path helpers ─────────────────────────────────────────────────────────────

/**
 * Map Potrace bitmap pixel coords back to SVG viewBox coordinates.
 */
function scaleAndOffsetPath(d, scale, offsetX, offsetY) {
  if (!d) return d;
  let coordIdx = 0;
  return d.replace(
    /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g,
    (n) => {
      const isX = coordIdx % 2 === 0;
      coordIdx++;
      const scaled = parseFloat(n) * scale;
      return (scaled + (isX ? offsetX : offsetY)).toFixed(2);
    },
  );
}

/**
 * Convert pen-tool anchor array to SVG `d` string (M / L / C / Z).
 * Identical logic to Canvas/index.js penPointsToD.
 */
function penPointsToD(points, closed) {
  if (!points || points.length === 0) return "";
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const ho = prev.handleOut;
    const hi = curr.handleIn;
    if (ho || hi) {
      const c1x = ho ? ho.x : prev.x;
      const c1y = ho ? ho.y : prev.y;
      const c2x = hi ? hi.x : curr.x;
      const c2y = hi ? hi.y : curr.y;
      d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${curr.x} ${curr.y}`;
    } else {
      d += ` L ${curr.x} ${curr.y}`;
    }
  }
  if (closed && points.length > 1) {
    const last = points[points.length - 1];
    const first = points[0];
    const ho = last.handleOut;
    const hi = first.handleIn;
    const c1x = ho ? ho.x : last.x;  const c1y = ho ? ho.y : last.y;
    const c2x = hi ? hi.x : first.x; const c2y = hi ? hi.y : first.y;
    if (ho || hi) d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${first.x} ${first.y}`;
    else          d += ` L ${first.x} ${first.y}`;
    d += " Z";
  }
  return d;
}

// Closest point on segment a→b to p (clamped to the segment).
function nearestPointOnSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return { x: a.x, y: a.y, dist: Math.hypot(p.x - a.x, p.y - a.y) };
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const sx = a.x + t * dx;
  const sy = a.y + t * dy;
  return { x: sx, y: sy, dist: Math.hypot(p.x - sx, p.y - sy) };
}

// Match `M x y L x y` (whitespace-separated, no comma) — what commitLineCut emits.
const SIMPLE_LINE_RE = /^M\s+(-?[\d.]+)\s+(-?[\d.]+)\s+L\s+(-?[\d.]+)\s+(-?[\d.]+)\s*$/;

const canvasPointToViewBox = (x, y, W, H, vbX, vbY, vbW, vbH) => ({
  x: vbX + (x / W) * vbW,
  y: vbY + (y / H) * vbH,
});

const viewBoxPointToCanvas = (x, y, W, H, vbX, vbY, vbW, vbH) => ({
  x: ((x - vbX) / vbW) * W,
  y: ((y - vbY) / vbH) * H,
});

const canvasPathToViewBox = (d, W, H, vbX, vbY, vbW, vbH) => {
  if (!d) return d;
  const m = d.match(SIMPLE_LINE_RE);
  if (m) {
    const a = canvasPointToViewBox(parseFloat(m[1]), parseFloat(m[2]), W, H, vbX, vbY, vbW, vbH);
    const b = canvasPointToViewBox(parseFloat(m[3]), parseFloat(m[4]), W, H, vbX, vbY, vbW, vbH);
    return `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} L ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
  }
  return d;
};

// Closest point on an arbitrary SVG `d` string to p. Uses fast segment math for
// straight `M…L` cuts and dense `getPointAtLength` sampling for pen curves.
function nearestPointOnPathD(d, p) {
  if (!d) return null;
  const m = d.match(SIMPLE_LINE_RE);
  if (m) {
    const a = { x: parseFloat(m[1]), y: parseFloat(m[2]) };
    const b = { x: parseFloat(m[3]), y: parseFloat(m[4]) };
    return nearestPointOnSegment(p, a, b);
  }
  try {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", d);
    const total = path.getTotalLength();
    if (!total || !isFinite(total)) return null;
    const STEP = Math.max(1, total / 200);
    let bestX = 0, bestY = 0, bestDist = Infinity;
    for (let l = 0; l <= total; l += STEP) {
      const pt = path.getPointAtLength(l);
      const dist = Math.hypot(p.x - pt.x, p.y - pt.y);
      if (dist < bestDist) { bestDist = dist; bestX = pt.x; bestY = pt.y; }
    }
    if (bestDist === Infinity) return null;
    return { x: bestX, y: bestY, dist: bestDist };
  } catch (_) {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────

// Screen-pixel drag threshold before treating pointer-move as handle creation
const DRAG_THRESHOLD_PX = 3;

/**
 * Precision "Cut" overlay for the stitch editor.
 *
 * Two modes:
 *  - Line : click start → hover to preview → click end to commit straight cut
 *  - Pen  : Illustrator-style Bézier pen — click to place anchors,
 *            click+drag for symmetric handles, Enter/double-click to commit,
 *            Escape to cancel, Backspace to remove last point, alt+click to
 *            break smooth on the last anchor.
 *
 * Uses synchronous Path2D rasterization; Potrace re-vectorizes on Apply.
 */
const StitchEraseCanvas = forwardRef(({
  svgJoined,
  layerIdx,
  colors,
  layoutMetrics,
  pan,
  zoom,
  initialCutMarks = [],
  onCutMarksChange,
  onComplete,
  onCancel,
  onDraftHistoryChange,
}, ref) => {
  const canvasRef  = useRef(null);
  const overlayRef = useRef(null);

  const [toolMode, setToolMode] = useState("line"); // "line" | "pen"

  // ── Line mode ──
  const [lineStart, setLineStart] = useState(null);
  const [hoverPos,  setHoverPos]  = useState(null);
  const draggingEndpoint = useRef(null); // "start" | "end" | null — which endpoint is being dragged
  const [lineEnd, setLineEnd] = useState(null); // persisted end point for drag adjustment
  // Live snap target on a previously committed cut mark (null when out of range).
  const [snapPos, setSnapPos] = useState(null);
  // Which committed cut line's delete handle is hovered (for visual feedback).
  const [hoverDeleteIdx, setHoverDeleteIdx] = useState(null);

  // ── Pen mode ──
  // Each point: { x, y, handleIn: {x,y}|null, handleOut: {x,y}|null }
  const [penPoints,   setPenPoints]   = useState([]);
  const [penMousePos, setPenMousePos] = useState(null);
  const penDraggingHandle = useRef(null); // { pointIndex } while dragging
  const penDragStart      = useRef(null); // { x, y } in clientX/Y for threshold
  const activePointerIdsRef = useRef(new Set());
  const ignorePointerSequenceRef = useRef(false);

  // ── Shared ──
  const [hasDrawn,    setHasDrawn]    = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [applyError,   setApplyError]   = useState(null);
  // Persistent cut lines in SVG viewBox coordinates — stable across canvas resyncs.
  const [cutMarks, setCutMarks] = useState(() => (
    Array.isArray(initialCutMarks) ? [...initialCutMarks] : []
  ));
  const [redoCutMarks, setRedoCutMarks] = useState([]);
  // Resolve the toolbar's controls slot after mount (it commits in the same pass
  // as this overlay, so it isn't in the DOM during our first render).
  const [controlsSlot, setControlsSlot] = useState(null);
  useEffect(() => {
    let raf;
    const find = () => {
      const el = document.getElementById("stitch-erase-controls-slot");
      if (el) { setControlsSlot(el); return; }
      raf = requestAnimationFrame(find);
    };
    find();
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, []);
  const cutMarksRef = useRef(Array.isArray(initialCutMarks) ? [...initialCutMarks] : []);
  const applyExitResolverRef = useRef(null);
  const applyStartRef = useRef(0);
  const applyEndTimerRef = useRef(null);
  const sessionModifiedRef = useRef(false);
  // Mirror in-progress line/pen state for synchronous replay before Apply.
  const lineStartRef = useRef(null);
  const lineEndRef = useRef(null);
  const penPointsRef = useRef([]);
  const toolModeRef = useRef("line");

  const { dw, dh, vbX, vbY, vbW, vbH, designLeft, designTop } = layoutMetrics;

  const W = Math.round(dw * ERASE_SCALE);
  const H = Math.round(dh * ERASE_SCALE);
  const scX = W / vbW;
  const scY = H / vbH;

  // Cut-gap width (in canvas bitmap px). Scales with the canvas/image size so the
  // slit stays proportionally minimal on small designs and large ones alike, with
  // a floor that's just wide enough that Potrace won't bridge the gap back on
  // re-vectorize (a 1px slit, ERASE_SCALE, reconnects).
  const cutLineWidth = Math.max(ERASE_SCALE * 1.5, Math.min(W, H) * 0.0025);

  useEffect(() => { cutMarksRef.current = cutMarks; }, [cutMarks]);
  useEffect(() => { lineStartRef.current = lineStart; }, [lineStart]);
  useEffect(() => { lineEndRef.current = lineEnd; }, [lineEnd]);
  useEffect(() => { penPointsRef.current = penPoints; }, [penPoints]);
  useEffect(() => { toolModeRef.current = toolMode; }, [toolMode]);

  // Convert a target screen-pixel size to SVG-viewBox units.
  // The overlay is scale(zoom)'d, so divide by zoom to keep things screen-constant.
  const px = (n) => (n * ERASE_SCALE) / zoom;

  // Close-path hit radius in canvas bitmap pixels (≈ 10 screen pixels)
  const closePx = px(10);

  // ── Collect path d-strings from the selected layer ───────────────────────

  const layerPaths = useMemo(() => {
    if (!svgJoined) return [];
    const layer = svgJoined.children?.[layerIdx];
    if (!layer) return [];
    const result = [];
    const walk = (node) => {
      if (!node) return;
      if (node.name === "path" && node.attributes?.d) { result.push(node.attributes.d); return; }
      (node.children || []).forEach(walk);
    };
    walk(layer);
    return result;
  }, [svgJoined, layerIdx]);

  // ── Synchronous canvas initialization (runs once on mount) ───────────────

  const drawBaseLayer = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layerPaths.length) return false;

    canvas.width  = W;
    canvas.height = H;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, W, H);

    const fill     = colors?.[layerIdx] || "#555555";
    const layer    = svgJoined?.children?.[layerIdx];
    const fillRule = layer?.attributes?.["fill-rule"] || "evenodd";

    const scX = W / vbW;
    const scY = H / vbH;
    ctx.save();
    ctx.setTransform(scX, 0, 0, scY, -vbX * scX, -vbY * scY);
    ctx.fillStyle = fill;
    layerPaths.forEach((d) => {
      try { ctx.fill(new Path2D(d), fillRule); } catch (_) { /* skip malformed paths */ }
    });
    ctx.restore();
    return true;
  }, [H, W, colors, layerIdx, layerPaths, svgJoined, vbH, vbW, vbX, vbY]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const pointerToCanvas = useCallback((e) => {
    const el = overlayRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width)  * W,
      y: ((e.clientY - rect.top)  / rect.height) * H,
    };
  }, [W, H]);

  // Erase an arbitrary SVG path string (viewBox coordinates) onto the bitmap canvas.
  const erasePath = useCallback((d) => {
    const canvas = canvasRef.current;
    if (!canvas || !d) return;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(scX, 0, 0, scY, -vbX * scX, -vbY * scY);
    ctx.globalCompositeOperation = "destination-out";
    ctx.strokeStyle = "rgba(0,0,0,1)";
    ctx.lineWidth = cutLineWidth / scX;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke(new Path2D(d));
    ctx.restore();
  }, [scX, scY, vbX, vbY, cutLineWidth]);

  const canvasPointsToViewBoxPath = useCallback((points, closed = false) => {
    if (!points?.length) return "";
    const vbPoints = points.map((pt) => {
      const base = canvasPointToViewBox(pt.x, pt.y, W, H, vbX, vbY, vbW, vbH);
      const handleIn = pt.handleIn
        ? canvasPointToViewBox(pt.handleIn.x, pt.handleIn.y, W, H, vbX, vbY, vbW, vbH)
        : null;
      const handleOut = pt.handleOut
        ? canvasPointToViewBox(pt.handleOut.x, pt.handleOut.y, W, H, vbX, vbY, vbW, vbH)
        : null;
      return { ...pt, x: base.x, y: base.y, handleIn, handleOut };
    });
    return penPointsToD(vbPoints, closed);
  }, [W, H, vbX, vbY, vbW, vbH]);

  const getPendingCutPathViewBox = useCallback(() => {
    if (toolModeRef.current === "line") {
      const start = lineStartRef.current;
      const end = lineEndRef.current;
      if (!start || !end) return null;
      const s = canvasPointToViewBox(start.x, start.y, W, H, vbX, vbY, vbW, vbH);
      const e = canvasPointToViewBox(end.x, end.y, W, H, vbX, vbY, vbW, vbH);
      return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} L ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
    }
    const pts = penPointsRef.current;
    if (pts.length >= 2) return canvasPointsToViewBoxPath(pts, false);
    return null;
  }, [W, H, vbX, vbY, vbW, vbH, canvasPointsToViewBoxPath]);

  const replayCutMarks = useCallback((marks, { includePending = false } = {}) => {
    drawBaseLayer();
    const paths = [...marks];
    if (includePending) {
      const pending = getPendingCutPathViewBox();
      if (pending) paths.push(pending);
    }
    paths.forEach((d) => erasePath(d));
  }, [drawBaseLayer, erasePath, getPendingCutPathViewBox]);

  const skipPersistCutMarksRef = useRef(true);
  const didRestoreCutMarksRef = useRef(false);

  // On mount, draw the base layer then replay any persisted cut lines.
  useEffect(() => {
    if (didRestoreCutMarksRef.current) return;
    didRestoreCutMarksRef.current = true;
    cutMarksRef.current = cutMarks;
    replayCutMarks(cutMarks);
    if (cutMarks.length > 0) setHasDrawn(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist cut-line edits to the parent (Firestore-bound stitch state).
  useEffect(() => {
    if (skipPersistCutMarksRef.current) {
      skipPersistCutMarksRef.current = false;
      return;
    }
    onCutMarksChange?.(cutMarks);
  }, [cutMarks, onCutMarksChange]);

  // When the underlying layer path changes (e.g. after Apply), redraw the base
  // shape and replay cut marks so the preview stays aligned with the SVG.
  const layerPathsKey = layerPaths.join("\x1e");
  useEffect(() => {
    if (!didRestoreCutMarksRef.current) return;
    drawBaseLayer();
    if (cutMarksRef.current.length) replayCutMarks(cutMarksRef.current);
  }, [layerPathsKey, drawBaseLayer, replayCutMarks]);

  const commitCutMark = useCallback((d, { space = "canvas" } = {}) => {
    if (!d) return;
    const viewBoxD = space === "viewBox"
      ? d
      : canvasPathToViewBox(d, W, H, vbX, vbY, vbW, vbH);
    erasePath(viewBoxD);
    setCutMarks((prev) => [...prev, viewBoxD]);
    setRedoCutMarks([]);
    setHasDrawn(true);
    sessionModifiedRef.current = true;
  }, [erasePath, W, H, vbX, vbY, vbW, vbH]);

  const undoDraftCut = useCallback(() => {
    setCutMarks((prev) => {
      if (!prev.length) return prev;
      const next = prev.slice(0, -1);
      const removed = prev[prev.length - 1];
      setRedoCutMarks((redo) => [removed, ...redo]);
      replayCutMarks(next);
      setHasDrawn(next.length > 0);
      sessionModifiedRef.current = true;
      return next;
    });
  }, [replayCutMarks]);

  const redoDraftCut = useCallback(() => {
    setRedoCutMarks((prevRedo) => {
      if (!prevRedo.length) return prevRedo;
      const [nextMark, ...remaining] = prevRedo;
      setCutMarks((prev) => {
        const next = [...prev, nextMark];
        replayCutMarks(next);
        setHasDrawn(true);
        sessionModifiedRef.current = true;
        return next;
      });
      return remaining;
    });
  }, [replayCutMarks]);

  // Remove one specific committed cut line. Pushes it onto the redo stack so it
  // can be brought back, then re-rasterizes from the base shape so the carved
  // slit for that line is healed.
  const deleteCutMark = useCallback((i) => {
    setCutMarks((prev) => {
      if (i < 0 || i >= prev.length) return prev;
      const removed = prev[i];
      const next = prev.filter((_, idx) => idx !== i);
      setRedoCutMarks((redo) => [removed, ...redo]);
      replayCutMarks(next);
      setHasDrawn(next.length > 0);
      sessionModifiedRef.current = true;
      return next;
    });
  }, [replayCutMarks]);

  // Midpoint of a committed cut path (viewBox coords) → canvas-bitmap coords,
  // for placing its delete handle. Straight cuts use the segment midpoint; pen
  // curves sample the path's middle.
  const cutMarkMidpoint = useCallback((d) => {
    if (!d) return null;
    const m = d.match(SIMPLE_LINE_RE);
    let vx, vy;
    if (m) {
      vx = (parseFloat(m[1]) + parseFloat(m[3])) / 2;
      vy = (parseFloat(m[2]) + parseFloat(m[4])) / 2;
    } else {
      try {
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", d);
        const total = path.getTotalLength();
        if (!total || !isFinite(total)) return null;
        const pt = path.getPointAtLength(total / 2);
        vx = pt.x; vy = pt.y;
      } catch (_) { return null; }
    }
    return viewBoxPointToCanvas(vx, vy, W, H, vbX, vbY, vbW, vbH);
  }, [W, H, vbX, vbY, vbW, vbH]);

  useEffect(() => {
    onDraftHistoryChange?.({
      canUndo: cutMarks.length > 0,
      canRedo: redoCutMarks.length > 0,
      undo: undoDraftCut,
      redo: redoDraftCut,
    });
  }, [cutMarks.length, redoCutMarks.length, undoDraftCut, redoDraftCut, onDraftHistoryChange]);

  useEffect(() => () => onDraftHistoryChange?.(null), [onDraftHistoryChange]);
  useEffect(() => () => {
    if (applyEndTimerRef.current) clearTimeout(applyEndTimerRef.current);
  }, []);

  // Commit the in-progress pen path to the canvas, then reset pen state
  const commitPenPath = useCallback((closed = false) => {
    if (penPoints.length < 2) {
      setPenPoints([]);
      setPenMousePos(null);
      penDraggingHandle.current = null;
      return;
    }
    const d = canvasPointsToViewBoxPath(penPoints, closed);
    if (d) {
      commitCutMark(d, { space: "viewBox" });
    }
    setPenPoints([]);
    setPenMousePos(null);
    penDraggingHandle.current = null;
  }, [penPoints, commitCutMark, canvasPointsToViewBoxPath]);

  // ── Pointer handlers ──────────────────────────────────────────────────────

  // Hit-test radius for grabbing endpoints (larger on touch for fat fingers)
  const isCoarse = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)")?.matches;
  const grabRadius = px(isCoarse ? 20 : 12);

  // Magnetic snap radius — only engages when the cursor is genuinely close to a
  // previously committed cut or the shape boundary. Tighter than `grabRadius`
  // so it doesn't fight the endpoint-drag hit test or feel grabby during normal drawing.
  const snapRadius = px(isCoarse ? 16 : 10);

  // Pre-sample the layer boundary paths into canvas-bitmap coordinates so we
  // can snap endpoints to the shape edge without calling getPointAtLength on
  // every move. Samples once when the layer or canvas dimensions change.
  const boundaryPointsRef = useRef([]);
  useEffect(() => {
    if (!layerPaths.length) { boundaryPointsRef.current = []; return; }
    const scX = W / vbW;
    const scY = H / vbH;
    const samples = [];
    for (const d of layerPaths) {
      try {
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", d);
        const total = path.getTotalLength();
        if (!total || !isFinite(total)) continue;
        const STEP = Math.max(1, total / 400);
        for (let l = 0; l <= total; l += STEP) {
          const pt = path.getPointAtLength(l);
          samples.push({
            x: (pt.x - vbX) * scX,
            y: (pt.y - vbY) * scY,
          });
        }
      } catch (_) { /* skip malformed paths */ }
    }
    boundaryPointsRef.current = samples;
  }, [layerPaths, W, H, vbW, vbH, vbX, vbY]);

  // Find the nearest boundary point within snapRadius.
  const findBoundarySnap = useCallback((p) => {
    const samples = boundaryPointsRef.current;
    if (!samples.length) return null;
    let bestX = 0, bestY = 0, bestDist = Infinity;
    for (let i = 0; i < samples.length; i++) {
      const s = samples[i];
      const dist = Math.hypot(p.x - s.x, p.y - s.y);
      if (dist < bestDist) { bestDist = dist; bestX = s.x; bestY = s.y; }
    }
    if (bestDist >= snapRadius) return null;
    return { x: bestX, y: bestY, dist: bestDist };
  }, [snapRadius]);

  // Find the nearest snap target across committed cuts AND the shape boundary.
  // Returns `{ x, y, dist }` in canvas coordinates, or null.
  const findSnapPoint = useCallback((pCanvas) => {
    let best = null;
    const pViewBox = canvasPointToViewBox(pCanvas.x, pCanvas.y, W, H, vbX, vbY, vbW, vbH);

    for (const d of cutMarks) {
      const np = nearestPointOnPathD(d, pViewBox);
      if (np && np.dist < snapRadius / scX && (!best || np.dist < best.dist)) {
        const canvasPt = viewBoxPointToCanvas(np.x, np.y, W, H, vbX, vbY, vbW, vbH);
        best = { x: canvasPt.x, y: canvasPt.y, dist: np.dist * scX };
      }
    }

    const bp = findBoundarySnap(pCanvas);
    if (bp && (!best || bp.dist < best.dist)) best = bp;

    return best;
  }, [cutMarks, snapRadius, scX, findBoundarySnap, W, H, vbX, vbY, vbW, vbH]);

  const onPointerDown = useCallback((e) => {
    if (isProcessing) return;
    e.preventDefault();
    e.stopPropagation();
    activePointerIdsRef.current.add(e.pointerId);
    if (e.pointerType === "touch" && activePointerIdsRef.current.size > 1) {
      ignorePointerSequenceRef.current = true;
      setLineStart(null);
      setLineEnd(null);
      setHoverPos(null);
      setSnapPos(null);
      draggingEndpoint.current = null;
      penDraggingHandle.current = null;
      penDragStart.current = null;
      return;
    }
    if (ignorePointerSequenceRef.current) return;
    const pos = pointerToCanvas(e);

    if (toolMode === "line") {
      // If both endpoints are placed, check if tapping near one to drag it
      if (lineStart && lineEnd) {
        const dStart = Math.hypot(pos.x - lineStart.x, pos.y - lineStart.y);
        const dEnd = Math.hypot(pos.x - lineEnd.x, pos.y - lineEnd.y);
        if (dStart < grabRadius) {
          draggingEndpoint.current = "start";
          e.currentTarget.setPointerCapture(e.pointerId);
          if (navigator.vibrate) navigator.vibrate(5);
          return;
        }
        if (dEnd < grabRadius) {
          draggingEndpoint.current = "end";
          e.currentTarget.setPointerCapture(e.pointerId);
          if (navigator.vibrate) navigator.vibrate(5);
          return;
        }
        // Tapped away from both endpoints — commit and start fresh.
        // Snap the new start to a nearby committed cut if applicable.
        const snap = findSnapPoint(pos);
        const startPos = snap ? { x: snap.x, y: snap.y } : pos;
        const d = `M ${lineStart.x} ${lineStart.y} L ${lineEnd.x} ${lineEnd.y}`;
        commitCutMark(d);
        setLineStart(startPos);
        setLineEnd(null);
        setHoverPos(null);
        setSnapPos(null);
        return;
      }
      // If only start is placed, check if tapping near it to drag
      if (lineStart && !lineEnd) {
        const dStart = Math.hypot(pos.x - lineStart.x, pos.y - lineStart.y);
        if (dStart < grabRadius) {
          draggingEndpoint.current = "start";
          e.currentTarget.setPointerCapture(e.pointerId);
          return;
        }
        // Place end point — snap to nearby committed cut if close enough.
        const snap = findSnapPoint(pos);
        setLineEnd(snap ? { x: snap.x, y: snap.y } : pos);
        setHoverPos(null);
        setSnapPos(null);
        return;
      }
      // No points yet — place start (snapped if near a committed cut).
      const snap = findSnapPoint(pos);
      setLineStart(snap ? { x: snap.x, y: snap.y } : pos);
      setSnapPos(null);
      return;
    }

    // ── Pen mode ──

    // Click near first point (> 2 pts) → close & commit
    if (penPoints.length > 2) {
      const first = penPoints[0];
      if (Math.hypot(pos.x - first.x, pos.y - first.y) < closePx) {
        commitPenPath(true);
        return;
      }
    }

    // Alt-click on last point → break smooth (remove outgoing handle)
    if (e.altKey && penPoints.length > 0) {
      const last = penPoints[penPoints.length - 1];
      if (Math.hypot(pos.x - last.x, pos.y - last.y) < closePx) {
        setPenPoints((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { ...copy[copy.length - 1], handleOut: null };
          return copy;
        });
        return;
      }
    }

    // Place new anchor
    e.currentTarget.setPointerCapture(e.pointerId);
    penDragStart.current = { x: e.clientX, y: e.clientY };
    setPenPoints((prev) => {
      penDraggingHandle.current = { pointIndex: prev.length };
      return [...prev, { x: pos.x, y: pos.y, handleIn: null, handleOut: null }];
    });
  }, [isProcessing, toolMode, lineStart, lineEnd, grabRadius, penPoints, closePx, pointerToCanvas, commitCutMark, commitPenPath, findSnapPoint]);

  const onPointerMove = useCallback((e) => {
    if (isProcessing) return;
    if (ignorePointerSequenceRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const pos = pointerToCanvas(e);

    if (toolMode === "line") {
      // Snap the live cursor to the nearest committed cut if within range.
      // Drives both endpoint-drag and the hover preview so the visible line
      // tracks exactly where a click would commit it.
      const snap = findSnapPoint(pos);
      const snapped = snap ? { x: snap.x, y: snap.y } : pos;
      setSnapPos(snap || null);

      if (draggingEndpoint.current === "start") {
        setLineStart(snapped);
        return;
      }
      if (draggingEndpoint.current === "end") {
        setLineEnd(snapped);
        return;
      }
      setHoverPos(snapped);
      return;
    }

    // Pen mode: always track cursor for rubber-band preview
    setPenMousePos(pos);

    if (!penDraggingHandle.current) return;

    // Enforce drag threshold before creating handles
    const start = penDragStart.current;
    if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) < DRAG_THRESHOLD_PX) return;

    const idx = penDraggingHandle.current.pointIndex;
    setPenPoints((prev) => {
      if (idx >= prev.length) return prev;
      const copy = [...prev];
      const pt   = copy[idx];
      const dx   = pos.x - pt.x;
      const dy   = pos.y - pt.y;
      copy[idx]  = {
        ...pt,
        handleOut: { x: pos.x,          y: pos.y           },
        handleIn:  { x: pt.x - dx,      y: pt.y - dy       },
      };
      return copy;
    });
  }, [isProcessing, toolMode, pointerToCanvas, findSnapPoint]);

  const onPointerUp = useCallback((e) => {
    activePointerIdsRef.current.delete(e.pointerId);
    if (activePointerIdsRef.current.size === 0) {
      ignorePointerSequenceRef.current = false;
    }
    draggingEndpoint.current  = null;
    penDraggingHandle.current = null;
    penDragStart.current      = null;
  }, []);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redoDraftCut();
        else undoDraftCut();
        return;
      }
      if (e.ctrlKey && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redoDraftCut();
        return;
      }
      if (e.key === "Escape") {
        setLineStart(null);
        setLineEnd(null);
        setHoverPos(null);
        setSnapPos(null);
        draggingEndpoint.current = null;
        setPenPoints([]);
        setPenMousePos(null);
        penDraggingHandle.current = null;
        return;
      }
      if (toolMode === "line") {
        if (e.key === "Backspace") {
          e.preventDefault();
          if (lineEnd) {
            setLineEnd(null);
          } else if (lineStart) {
            setLineStart(null);
            setHoverPos(null);
          } else {
            undoDraftCut();
          }
        }
        // Enter commits the current line if both points placed
        if (e.key === "Enter" && lineStart && lineEnd) {
          const d = `M ${lineStart.x} ${lineStart.y} L ${lineEnd.x} ${lineEnd.y}`;
          commitCutMark(d);
          setLineStart(null);
          setLineEnd(null);
          setHoverPos(null);
          setSnapPos(null);
        }
        return;
      }
      if (e.key === "Enter") {
        commitPenPath(false);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        if (penPoints.length > 0) setPenPoints((prev) => prev.slice(0, -1));
        else undoDraftCut();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toolMode, lineStart, lineEnd, commitPenPath, commitCutMark, penPoints.length, undoDraftCut, redoDraftCut]);

  // Switch tool — clear all in-progress drawing state
  const switchMode = useCallback((mode) => {
    setToolMode(mode);
    setLineStart(null);
    setLineEnd(null);
    setHoverPos(null);
    setSnapPos(null);
    draggingEndpoint.current = null;
    setPenPoints([]);
    setPenMousePos(null);
    penDraggingHandle.current = null;
  }, []);

  // Double-click → commit open path
  const onDoubleClick = useCallback((e) => {
    if (toolMode === "pen" && penPoints.length >= 2) {
      e.preventDefault();
      e.stopPropagation();
      // Drop the duplicate anchor from the double-click
      const pts = penPoints.slice(0, -1);
      const d   = canvasPointsToViewBoxPath(pts, false);
      if (d) commitCutMark(d, { space: "viewBox" });
      setPenPoints([]);
      setPenMousePos(null);
      penDraggingHandle.current = null;
    }
  }, [toolMode, penPoints, commitCutMark, canvasPointsToViewBoxPath]);

  // ── Pen preview geometry ──────────────────────────────────────────────────

  const penCommittedD = penPoints.length > 0 ? penPointsToD(penPoints, false) : "";

  const penPreviewD = (() => {
    if (!penMousePos || penPoints.length === 0) return "";
    const last = penPoints[penPoints.length - 1];
    const ho   = last.handleOut;
    if (ho) {
      // Mirror the outgoing handle to predict incoming, same as the SVG editor
      return `M ${last.x} ${last.y} C ${ho.x} ${ho.y} ${penMousePos.x} ${penMousePos.y} ${penMousePos.x} ${penMousePos.y}`;
    }
    return `M ${last.x} ${last.y} L ${penMousePos.x} ${penMousePos.y}`;
  })();

  const isNearFirst = penPoints.length > 2 && penMousePos &&
    Math.hypot(penMousePos.x - penPoints[0].x, penMousePos.y - penPoints[0].y) < closePx;

  // Anchor / handle sizes — zoom-normalized so they stay constant on screen
  const ANCHOR_R = px(4);  // 4 screen-pixel half-size square
  const HANDLE_R = px(3);  // 3 screen-pixel radius circle
  const SW       = px(1);  // 1 screen-pixel stroke width
  const SW_HALO  = px(3);  // 3 screen-pixel halo stroke

  // ── Apply (Potrace) ───────────────────────────────────────────────────────

  const hasPendingCut = () => !!(lineStartRef.current && lineEndRef.current)
    || penPointsRef.current.length >= 2;

  const hasWorkToApply = useCallback(() => {
    return cutMarksRef.current.length > 0 || hasPendingCut();
  }, []);

  const handleApply = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      applyExitResolverRef.current?.(false);
      applyExitResolverRef.current = null;
      return;
    }
    setIsProcessing(true);
    setApplyError(null);
    applyStartRef.current = Date.now();

    // Always rebuild from the canonical stored cut lines so every mark is applied
    // at its preserved viewBox location — never rely on whatever happens to be
    // on the bitmap canvas from incremental erases.
    replayCutMarks(cutMarksRef.current, { includePending: true });

    let bwCanvas;
    try {
      const ctx       = canvas.getContext("2d");
      const imageData = ctx.getImageData(0, 0, W, H);

      bwCanvas        = document.createElement("canvas");
      bwCanvas.width  = W;
      bwCanvas.height = H;
      const bwCtx     = bwCanvas.getContext("2d");
      bwCtx.fillStyle = "#ffffff";
      bwCtx.fillRect(0, 0, W, H);
      const bwData    = bwCtx.getImageData(0, 0, W, H);
      for (let i = 0; i < imageData.data.length; i += 4) {
        if (imageData.data[i + 3] > 10) {
          bwData.data[i] = bwData.data[i + 1] = bwData.data[i + 2] = 0;
          bwData.data[i + 3] = 255;
        }
      }
      bwCtx.putImageData(bwData, 0, 0);
    } catch (err) {
      console.error("[StitchEraseCanvas] Canvas read error:", err);
      setIsProcessing(false);
      setApplyError("Could not read canvas. Try again.");
      applyExitResolverRef.current?.(false);
      applyExitResolverRef.current = null;
      return;
    }

    bwCanvas.toBlob((blob) => {
      if (!blob) {
        setIsProcessing(false);
        setApplyError("Failed to encode canvas. Try again.");
        applyExitResolverRef.current?.(false);
        applyExitResolverRef.current = null;
        return;
      }
      const blobUrl   = URL.createObjectURL(blob);
      const timeoutId = setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        setIsProcessing(false);
        setApplyError("Vectorization timed out. Try again.");
        applyExitResolverRef.current?.(false);
        applyExitResolverRef.current = null;
      }, 15000);

      const trace = new Potrace.Potrace();
      trace.setParameters({ turdSize: 2, optTolerance: 0.2, alphaMax: 1, threshold: 128 });

      trace.loadImage(blobUrl, (loadErr) => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(blobUrl);
        if (loadErr) {
          setIsProcessing(false);
          setApplyError("Vectorization failed. Try again.");
          applyExitResolverRef.current?.(false);
          applyExitResolverRef.current = null;
          return;
        }
        try {
          const pathTag = trace.getPathTag();
          const match   = pathTag?.match(/d="([^"]+)"/);
          if (!match) {
            finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
              onComplete?.(layerIdx, "");
              applyExitResolverRef.current?.(true);
              applyExitResolverRef.current = null;
            });
            return;
          }
          const newPathD = scaleAndOffsetPath(match[1], vbW / W, vbX, vbY);
          finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
            sessionModifiedRef.current = false;
            onComplete?.(layerIdx, newPathD);
            applyExitResolverRef.current?.(true);
            applyExitResolverRef.current = null;
          });
        } catch (err) {
          console.error("[StitchEraseCanvas] Potrace error:", err);
          setIsProcessing(false);
          setApplyError("Vectorization failed. Try again.");
          applyExitResolverRef.current?.(false);
          applyExitResolverRef.current = null;
        }
      });
    }, "image/png");
  }, [W, H, vbX, vbY, vbW, layerIdx, onComplete, replayCutMarks]);

  useImperativeHandle(ref, () => ({
    hasWorkToApply,
    applyAndExit: () => new Promise((resolve) => {
      if (isProcessing) {
        resolve(false);
        return;
      }
      if (!hasWorkToApply()) {
        resolve(false);
        return;
      }
      applyExitResolverRef.current = resolve;
      handleApply();
    }),
  }), [hasWorkToApply, isProcessing, handleApply]);

  // ── Styles ────────────────────────────────────────────────────────────────

  const cursor = isProcessing ? "wait" : toolMode === "pen" ? "crosshair" : "crosshair";

  const btnBase = {
    border: "1px solid rgba(0,0,0,0.1)",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "Clash Grotesk, sans-serif",
    fontWeight: 600,
    fontSize: "11px",
    padding: "4px 10px",
    transition: "all 0.12s ease",
  };

  // Commit the current line cut and reset
  const commitLineCut = useCallback(() => {
    if (!lineStart || !lineEnd) return;
    const d = `M ${lineStart.x} ${lineStart.y} L ${lineEnd.x} ${lineEnd.y}`;
    commitCutMark(d);
    setLineStart(null);
    setLineEnd(null);
    setHoverPos(null);
    setSnapPos(null);
    if (navigator.vibrate) navigator.vibrate(10);
  }, [lineStart, lineEnd, commitCutMark]);

  const hasPendingCutVisible = !!(lineStart && lineEnd) || penPoints.length >= 2;
  const showCutAndApply = cutMarks.length > 0 || hasPendingCutVisible;

  const modeHint = () => {
    if (applyError) return applyError;
    if (toolMode === "line") {
      if (lineStart && lineEnd) return isCoarse ? "Drag the dots to adjust, then Cut & Apply" : "Drag the endpoints to adjust, then Cut & Apply";
      if (lineStart) return isCoarse ? "Now tap just past the opposite edge" : "Now click just past the opposite edge — cross all the way through";
      if (cutMarks.length > 0) return isCoarse ? "Tap to add another cut · tap × to remove" : "Click to add another cut · click × to remove";
      return isCoarse ? "Tap just outside one edge to start" : "Click just outside one edge to start";
    }
    if (penPoints.length === 0) {
      return isCoarse ? "Tap the edge to place the first point" : "Click the edge to place the first point";
    }
    if (penPoints.length === 1) {
      return isCoarse ? "Tap to add points · drag for curves" : "Click to add points · drag for curves";
    }
    return isCoarse
      ? "Tap ✓ Finish when the curve looks right"
      : "Click ✓ Finish when done (or double-click) · ⌫ remove last point";
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Canvas + SVG preview overlay */}
      <div
        ref={overlayRef}
        style={{
          position: "absolute",
          top: 0, left: 0,
          width:  `${dw}px`,
          height: `${dh}px`,
          transformOrigin: "0 0",
          transform: `translate(${pan.x + designLeft * zoom}px, ${pan.y + designTop * zoom}px) scale(${zoom})`,
          cursor,
          zIndex: 50,
          touchAction: "none",
          userSelect: "none",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        onDoubleClick={onDoubleClick}
      >
        {/* Rasterized layer — erased live with destination-out */}
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", display: "block" }}
        />

        {/* ── SVG preview overlay (same coordinate space as canvas bitmap) ── */}
        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          {/* Committed cut lines — kept visible so every cut persists on screen
              (the bitmap only carves a thin slit, which is easy to lose). The
              group maps viewBox-space cut paths into the canvas-bitmap space. */}
          {cutMarks.length > 0 && (
            <g transform={`scale(${W / vbW} ${H / vbH}) translate(${-vbX} ${-vbY})`}>
              {cutMarks.map((d, i) => (
                <g key={`cutmark-${i}`}>
                  <path d={d} fill="none" stroke="#fff" strokeWidth={3}
                    vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
                  <path d={d} fill="none" stroke="#7C3AED" strokeWidth={1.5}
                    vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" opacity={0.95} />
                </g>
              ))}
            </g>
          )}

          {/* ── LINE mode ── */}
          {/* Crosshair cursor when no points placed */}
          {toolMode === "line" && hoverPos && !lineStart && (
            <>
              <line x1={hoverPos.x - SW * 6} y1={hoverPos.y} x2={hoverPos.x + SW * 6} y2={hoverPos.y}
                stroke="#fff" strokeWidth={SW_HALO} strokeLinecap="round" opacity="0.7" />
              <line x1={hoverPos.x} y1={hoverPos.y - SW * 6} x2={hoverPos.x} y2={hoverPos.y + SW * 6}
                stroke="#fff" strokeWidth={SW_HALO} strokeLinecap="round" opacity="0.7" />
              <line x1={hoverPos.x - SW * 6} y1={hoverPos.y} x2={hoverPos.x + SW * 6} y2={hoverPos.y}
                stroke="#7C3AED" strokeWidth={SW} opacity="0.9" />
              <line x1={hoverPos.x} y1={hoverPos.y - SW * 6} x2={hoverPos.x} y2={hoverPos.y + SW * 6}
                stroke="#7C3AED" strokeWidth={SW} opacity="0.9" />
            </>
          )}
          {/* Preview line: start → end (solid) or start → hover (dashed) */}
          {toolMode === "line" && lineStart && (lineEnd || hoverPos) && (() => {
            const target = lineEnd || hoverPos;
            const isFinal = !!lineEnd;
            return (
              <>
                <line x1={lineStart.x} y1={lineStart.y} x2={target.x} y2={target.y}
                  stroke="#fff" strokeWidth={SW_HALO} strokeLinecap="round" opacity="0.85" />
                <line x1={lineStart.x} y1={lineStart.y} x2={target.x} y2={target.y}
                  stroke="#7C3AED" strokeWidth={isFinal ? SW * 1.5 : SW}
                  strokeDasharray={isFinal ? "none" : `${SW * 6} ${SW * 3}`} opacity="1" />
              </>
            );
          })()}
          {/* Draggable start endpoint */}
          {toolMode === "line" && lineStart && (
            <>
              {/* Invisible large touch target */}
              <circle cx={lineStart.x} cy={lineStart.y} r={grabRadius}
                fill="transparent" stroke="none" style={{ pointerEvents: "auto", cursor: "grab" }} />
              <circle cx={lineStart.x} cy={lineStart.y} r={ANCHOR_R * 1.8} fill="#fff" opacity="0.95" />
              <circle cx={lineStart.x} cy={lineStart.y} r={ANCHOR_R * 1.2} fill="#7C3AED" opacity="0.95" />
              <circle cx={lineStart.x} cy={lineStart.y} r={ANCHOR_R * 0.4} fill="#fff" opacity="0.9" />
            </>
          )}
          {/* Draggable end endpoint */}
          {toolMode === "line" && lineEnd && (
            <>
              <circle cx={lineEnd.x} cy={lineEnd.y} r={grabRadius}
                fill="transparent" stroke="none" style={{ pointerEvents: "auto", cursor: "grab" }} />
              <circle cx={lineEnd.x} cy={lineEnd.y} r={ANCHOR_R * 1.8} fill="#fff" opacity="0.95" />
              <circle cx={lineEnd.x} cy={lineEnd.y} r={ANCHOR_R * 1.2} fill="#ef4444" opacity="0.95" />
              <circle cx={lineEnd.x} cy={lineEnd.y} r={ANCHOR_R * 0.4} fill="#fff" opacity="0.9" />
            </>
          )}

          {/* Snap-to-cut indicator: highlights the point on a previously
              committed cut that the cursor is locked onto. */}
          {toolMode === "line" && snapPos && (
            <g pointerEvents="none">
              <circle cx={snapPos.x} cy={snapPos.y} r={ANCHOR_R * 2.4}
                fill="none" stroke="#fff" strokeWidth={SW_HALO} opacity="0.85" />
              <circle cx={snapPos.x} cy={snapPos.y} r={ANCHOR_R * 2.4}
                fill="none" stroke="#7C3AED" strokeWidth={SW * 1.4} opacity="0.95"
                strokeDasharray={`${SW * 3} ${SW * 2}`} />
              <circle cx={snapPos.x} cy={snapPos.y} r={ANCHOR_R * 0.5}
                fill="#7C3AED" opacity="0.9" />
            </g>
          )}

          {/* ── PEN mode ── */}
          {toolMode === "pen" && (
            <>
              {penCommittedD && (
                <>
                  <path d={penCommittedD} fill="none"
                    stroke="#fff" strokeWidth={SW_HALO} strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
                  <path d={penCommittedD} fill="none"
                    stroke="#7C3AED" strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" opacity="1" />
                </>
              )}
              {penPreviewD && (
                <>
                  <path d={penPreviewD} fill="none"
                    stroke="#fff" strokeWidth={SW_HALO} strokeLinecap="round" opacity="0.7" />
                  <path d={penPreviewD} fill="none"
                    stroke="#7C3AED" strokeWidth={SW}
                    strokeDasharray={`${SW * 6} ${SW * 3}`} opacity="1" />
                </>
              )}

              {penPoints.map((pt, i) => (
                <g key={i}>
                  {pt.handleIn && (
                    <>
                      <line x1={pt.x} y1={pt.y} x2={pt.handleIn.x} y2={pt.handleIn.y}
                        stroke="#fff" strokeWidth={SW_HALO} opacity="0.6" />
                      <line x1={pt.x} y1={pt.y} x2={pt.handleIn.x} y2={pt.handleIn.y}
                        stroke="#7C3AED" strokeWidth={SW} opacity="0.9" />
                      <circle cx={pt.handleIn.x} cy={pt.handleIn.y} r={HANDLE_R * 1.4} fill="#fff" opacity="0.9" />
                      <circle cx={pt.handleIn.x} cy={pt.handleIn.y} r={HANDLE_R}       fill="#7C3AED" opacity="0.9" />
                    </>
                  )}
                  {pt.handleOut && (
                    <>
                      <line x1={pt.x} y1={pt.y} x2={pt.handleOut.x} y2={pt.handleOut.y}
                        stroke="#fff" strokeWidth={SW_HALO} opacity="0.6" />
                      <line x1={pt.x} y1={pt.y} x2={pt.handleOut.x} y2={pt.handleOut.y}
                        stroke="#7C3AED" strokeWidth={SW} opacity="0.9" />
                      <circle cx={pt.handleOut.x} cy={pt.handleOut.y} r={HANDLE_R * 1.4} fill="#fff" opacity="0.9" />
                      <circle cx={pt.handleOut.x} cy={pt.handleOut.y} r={HANDLE_R}       fill="#7C3AED" opacity="0.9" />
                    </>
                  )}
                  {/* Anchor: filled square; first anchor gets close-path ring when cursor is near */}
                  {i === 0 && penPoints.length > 2 ? (
                    <>
                      <rect x={pt.x - ANCHOR_R} y={pt.y - ANCHOR_R}
                        width={ANCHOR_R * 2} height={ANCHOR_R * 2}
                        fill="#7C3AED" opacity="0.9" rx={1} />
                      <circle cx={pt.x} cy={pt.y} r={closePx}
                        fill="none" stroke="#7C3AED" strokeWidth={SW}
                        opacity={isNearFirst ? 0.9 : 0.3}
                        strokeDasharray={isNearFirst ? "none" : `${SW * 4} ${SW * 2}`} />
                    </>
                  ) : (
                    <rect x={pt.x - ANCHOR_R} y={pt.y - ANCHOR_R}
                      width={ANCHOR_R * 2} height={ANCHOR_R * 2}
                      fill={i === penPoints.length - 1 ? "#fff" : "#7C3AED"}
                      stroke="#7C3AED" strokeWidth={SW}
                      opacity="0.9" rx={1} />
                  )}
                </g>
              ))}

              {/* Floating "Finish" pill above the last point — the clear,
                  beginner-friendly way to stop drawing the curve. */}
              {penPoints.length >= 2 && (() => {
                const last = penPoints[penPoints.length - 1];
                const w = px(74), h = px(26);
                const cx = last.x;
                const cy = last.y - px(24) - h / 2;
                return (
                  <g
                    style={{ pointerEvents: "auto", cursor: "pointer" }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); commitPenPath(false); }}
                  >
                    <title>Finish the curve</title>
                    <line x1={last.x} y1={last.y} x2={cx} y2={cy + h / 2}
                      stroke="#7C3AED" strokeWidth={SW} opacity="0.5" />
                    <rect x={cx - w / 2} y={cy - h / 2} width={w} height={h} rx={h / 2}
                      fill="#7C3AED" stroke="#fff" strokeWidth={SW * 1.2} />
                    <text x={cx} y={cy} fontSize={px(12)} fontWeight="700" fill="#fff"
                      textAnchor="middle" dominantBaseline="central"
                      style={{ fontFamily: "Clash Grotesk, sans-serif", userSelect: "none" }}>
                      ✓ Finish
                    </text>
                  </g>
                );
              })()}

              {penPoints.length === 0 && penMousePos && (
                <>
                  <line x1={penMousePos.x - SW * 6} y1={penMousePos.y} x2={penMousePos.x + SW * 6} y2={penMousePos.y}
                    stroke="#fff" strokeWidth={SW_HALO} strokeLinecap="round" opacity="0.7" />
                  <line x1={penMousePos.x} y1={penMousePos.y - SW * 6} x2={penMousePos.x} y2={penMousePos.y + SW * 6}
                    stroke="#fff" strokeWidth={SW_HALO} strokeLinecap="round" opacity="0.7" />
                  <line x1={penMousePos.x - SW * 6} y1={penMousePos.y} x2={penMousePos.x + SW * 6} y2={penMousePos.y}
                    stroke="#7C3AED" strokeWidth={SW} opacity="0.9" />
                  <line x1={penMousePos.x} y1={penMousePos.y - SW * 6} x2={penMousePos.x} y2={penMousePos.y + SW * 6}
                    stroke="#7C3AED" strokeWidth={SW} opacity="0.9" />
                </>
              )}
            </>
          )}
          {/* ── Committed cut markers — stored in viewBox, drawn in canvas space ── */}
          <g transform={`translate(${-vbX * scX} ${-vbY * scY}) scale(${scX} ${scY})`}>
            {cutMarks.map((d, i) => (
              <g key={i}>
                <path d={d} fill="none"
                  stroke="#fff" strokeWidth={SW_HALO / scX}
                  strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
                <path d={d} fill="none"
                  stroke="#7C3AED" strokeWidth={SW / scX}
                  strokeLinecap="round" strokeLinejoin="round" opacity="1" />
              </g>
            ))}
          </g>

          {/* ── Per-line delete handles — a red × at each committed cut's midpoint
              so any drawn line can be removed individually (not just undo-last). ── */}
          {cutMarks.map((d, i) => {
            // Don't sit a delete handle on top of the pending line's endpoints.
            if (toolMode === "line" && (draggingEndpoint.current)) return null;
            const mid = cutMarkMidpoint(d);
            if (!mid) return null;
            const hovered = hoverDeleteIdx === i;
            const r = px(hovered ? 9 : 7.5);
            const xr = px(hovered ? 4 : 3.3);
            return (
              <g
                key={`cut-del-${i}`}
                style={{ pointerEvents: "auto", cursor: "pointer" }}
                onPointerDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); deleteCutMark(i); }}
                onMouseEnter={() => setHoverDeleteIdx(i)}
                onMouseLeave={() => setHoverDeleteIdx((cur) => (cur === i ? null : cur))}
              >
                <title>Remove this cut line</title>
                {/* Enlarged invisible hit target */}
                <circle cx={mid.x} cy={mid.y} r={px(15)} fill="transparent" />
                <circle cx={mid.x} cy={mid.y} r={r}
                  fill={hovered ? "#ef4444" : "#fff"}
                  stroke="#ef4444" strokeWidth={SW * 1.2} />
                <line x1={mid.x - xr} y1={mid.y - xr} x2={mid.x + xr} y2={mid.y + xr}
                  stroke={hovered ? "#fff" : "#ef4444"} strokeWidth={SW * 1.6} strokeLinecap="round" />
                <line x1={mid.x - xr} y1={mid.y + xr} x2={mid.x + xr} y2={mid.y - xr}
                  stroke={hovered ? "#fff" : "#ef4444"} strokeWidth={SW * 1.6} strokeLinecap="round" />
              </g>
            );
          })}
        </svg>
      </div>

      {/* Control bar — portaled into the toolbar slot at the top */}
      {controlsSlot && createPortal(
          <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "10px" }}>
            {/* Illustrated step-by-step walkthrough for first-timers */}
            <SplitVisualTutorial coarse={isCoarse} />

            {/* Status */}
            <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <div style={{
                width: "6px", height: "6px", borderRadius: "50%",
                background: applyError ? "#dc2626" : "#7C3AED",
                animation: "pulse-dot 1.2s ease-in-out infinite",
                flexShrink: 0,
              }} />
              <span style={{ fontSize: "11px", fontWeight: 500, color: applyError ? "#dc2626" : "#555", lineHeight: 1.35 }}>
                {modeHint()}
              </span>
            </div>

            {/* Cut shape: straight line or freehand curve */}
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                data-tutorial="split-mode-line"
                onClick={() => switchMode("line")}
                style={{
                  ...btnBase,
                  flex: 1,
                  padding: "7px 8px",
                  background: toolMode === "line" ? "#f3e8ff" : "transparent",
                  color:      toolMode === "line" ? "#7C3AED" : "#888",
                  border:     toolMode === "line" ? "1px solid #d8b4fe" : "1px solid rgba(0,0,0,0.1)",
                }}
              >
                / Line
              </button>
              <button
                data-tutorial="split-mode-curve"
                onClick={() => switchMode("pen")}
                style={{
                  ...btnBase,
                  flex: 1,
                  padding: "7px 8px",
                  background: toolMode === "pen" ? "#f3e8ff" : "transparent",
                  color:      toolMode === "pen" ? "#7C3AED" : "#888",
                  border:     toolMode === "pen" ? "1px solid #d8b4fe" : "1px solid rgba(0,0,0,0.1)",
                }}
              >
                ✦ Curve
              </button>
            </div>

            {/* Curve-in-progress controls — explicit, beginner-friendly way to
                stop drawing instead of relying on double-click / Enter. */}
            {toolMode === "pen" && penPoints.length >= 1 && (
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  onClick={() => commitPenPath(false)}
                  disabled={penPoints.length < 2}
                  style={{
                    ...btnBase,
                    flex: 1,
                    border: "none",
                    background: penPoints.length < 2 ? "#ede9fe" : "linear-gradient(135deg, #7C3AED, #6d28d9)",
                    color: penPoints.length < 2 ? "#b69bf0" : "#fff",
                    cursor: penPoints.length < 2 ? "default" : "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "5px",
                    padding: "7px 10px",
                  }}
                >
                  ✓ Finish curve
                </button>
                <button
                  onClick={() => setPenPoints((prev) => prev.slice(0, -1))}
                  title="Remove the last point"
                  style={{ ...btnBase, flex: "0 0 auto", padding: "7px 10px", background: "#fff", color: "#888" }}
                >
                  ↶ Undo point
                </button>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: "8px" }}>
              {showCutAndApply && (
                <button
                  data-tutorial="split-cut-apply"
                  onClick={handleApply}
                  disabled={isProcessing}
                  style={{
                    ...btnBase,
                    flex: 1,
                    border: "none",
                    background: isProcessing
                      ? "#e5d4ff"
                      : "linear-gradient(135deg, #7C3AED, #6d28d9)",
                    color: isProcessing ? "#9c6df5" : "#fff",
                    cursor: isProcessing ? "wait" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    padding: "7px 12px",
                  }}
                >
                  {isProcessing ? (
                    <>
                      <span style={{
                        width: "9px", height: "9px", borderRadius: "50%",
                        border: "2px solid rgba(156,109,245,0.3)",
                        borderTop: "2px solid #9c6df5",
                        animation: "stitch-ai-spin 0.7s linear infinite",
                        display: "inline-block", flexShrink: 0,
                      }} />
                      Processing…
                    </>
                  ) : "✂ Cut & Apply"}
                </button>
              )}

              <button
                data-tutorial="split-cancel"
                onClick={onCancel}
                style={{ ...btnBase, flex: showCutAndApply ? "0 0 auto" : 1, padding: "7px 12px", background: "#fff", color: "#888" }}
              >
                Cancel
              </button>
            </div>
          </div>,
          controlsSlot,
        )}
    </>
  );
});

StitchEraseCanvas.displayName = "StitchEraseCanvas";

export default StitchEraseCanvas;
